import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleServiceChange = (value) => {
    setFormData({
      ...formData,
      service: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission - store in browser's localStorage
    setTimeout(() => {
      const submissions = JSON.parse(
        localStorage.getItem('contactSubmissions') || '[]'
      );
      const newSubmission = {
        ...formData,
        id: Date.now(),
        timestamp: new Date().toISOString(),
      };
      submissions.push(newSubmission);
      localStorage.setItem('contactSubmissions', JSON.stringify(submissions));

      toast.success("Message sent successfully! We'll contact you soon.");

      // Reset form
      setFormData({
        name: '',
        phone: '',
        service: '',
        message: '',
      });
      setIsSubmitting(false);
    }, 1000);
  };

  // WhatsApp click handler
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      'Hello! I would like to inquire about your AC services.'
    );
    window.open(`https://wa.me/9507331675?text=${message}`, '_blank');
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="text-center mb-16 space-y-4">
          <span className="text-primary font-semibold text-sm uppercase tracking-wider">
            Contact Us
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
            Get In Touch
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Have questions or need a quote? Fill out the form below and our team
            will get back to you shortly.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Contact Information
              </h3>
              <div className="space-y-6">
                <a
                  href="tel:+919507331675"
                  className="flex items-start gap-4 p-4 rounded-xl hover:bg-white transition-colors group"
                >
                  <div className="bg-primary/10 p-3 rounded-lg group-hover:bg-primary group-hover:scale-110 transition-all">
                    <Phone
                      className="text-primary group-hover:text-white"
                      size={24}
                    />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Phone</p>
                    <p className="text-lg font-semibold text-gray-900 group-hover:text-primary">
                      +91 9507331675
                    </p>
                  </div>
                </a>

                <a
                  href="mailto:info@coolantairsystems.com"
                  className="flex items-start gap-4 p-4 rounded-xl hover:bg-white transition-colors group"
                >
                  <div className="bg-primary/10 p-3 rounded-lg group-hover:bg-primary group-hover:scale-110 transition-all">
                    <Mail
                      className="text-primary group-hover:text-white"
                      size={24}
                    />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Email</p>
                    <p className="text-lg font-semibold text-gray-900 group-hover:text-primary">
                      info@coolantairsystems.com
                    </p>
                  </div>
                </a>

                <a
                  href="https://coolantairsystems.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-4 rounded-xl hover:bg-white transition-colors group"
                >
                  <div className="bg-primary/10 p-3 rounded-lg group-hover:bg-primary group-hover:scale-110 transition-all">
                    <MapPin
                      className="text-primary group-hover:text-white"
                      size={24}
                    />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Website</p>
                    <p className="text-lg font-semibold text-gray-900 group-hover:text-primary">
                      coolantairsystems.com
                    </p>
                  </div>
                </a>
              </div>
            </div>

            {/* WhatsApp Button */}
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <MessageSquare className="text-green-600" size={28} />
                <div>
                  <h4 className="font-bold text-gray-900">
                    Quick Chat on WhatsApp
                  </h4>
                  <p className="text-sm text-gray-600">Get instant response</p>
                </div>
              </div>
              <Button
                onClick={handleWhatsAppClick}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                size="lg"
              >
                <MessageSquare className="mr-2" size={20} />
                Chat on WhatsApp
              </Button>
            </div>
          </div>
          {/* Google Maps */}
          <div className="bg-white p-2 rounded-2xl shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d241317.11609823277!2d72.71637344999999!3d19.08219855!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c6306644edc1%3A0x5da4ed8f8d648c69!2sMumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1234567890"
              width="100%"
              height="500"
              style={{ border: 0, borderRadius: '12px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Google Maps Location"
            ></iframe>
          </div>
          {/* Contact Form */}
          {/* <div className="bg-white p-8 rounded-2xl shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Send us a Message
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="w-full"
                />
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Phone Number *
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Your phone number"
                  className="w-full"
                />
              </div>

              <div>
                <label
                  htmlFor="service"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Service Required *
                </label>
                <Select
                  value={formData.service}
                  onValueChange={handleServiceChange}
                  required
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ac-sales">AC Sales</SelectItem>
                    <SelectItem value="ac-installation">
                      AC Installation
                    </SelectItem>
                    <SelectItem value="ac-repair">AC Repair</SelectItem>
                    <SelectItem value="amc">
                      Annual Maintenance (AMC)
                    </SelectItem>
                    <SelectItem value="commercial-hvac">
                      Commercial HVAC
                    </SelectItem>
                    <SelectItem value="gas-refilling">
                      Gas Refilling & Cleaning
                    </SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your requirements..."
                  className="w-full"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-white"
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="mr-2" size={20} />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div> */}
        </div>
      </div>
    </section>
  );
};

export default Contact;
